import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../services/adminApi";
import usePolling from "../hooks/usePolling";
import Pagination from "../components/Pagination";

export default function WithdrawalManagement() {
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 10 });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [list, summary] = await Promise.all([
        adminApi.getWithdrawals({
          page: data.page,
          pageSize: data.pageSize,
          status: status || undefined,
        }),
        adminApi.getWithdrawalStats(),
      ]);
      setData(list);
      setStats(summary);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load withdrawals");
    }
  }, [status, data.page, data.pageSize]);

  usePolling(load, 15000, [status, data.page, data.pageSize]);

  useEffect(() => {
    setData((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [status]);

  const approve = async (id) => {
    try {
      await adminApi.approveWithdrawal(id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Approval failed");
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason");
    if (!reason) return;
    try {
      await adminApi.rejectWithdrawal(id, reason);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Rejection failed");
    }
  };

  const retry = async (id) => {
    try {
      await adminApi.retryWithdrawal(id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Retry failed");
    }
  };

  return (
    <div>
      <h2>Withdrawal Management</h2>
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          <option value="PENDING_ADMIN_APPROVAL">Pending</option>
          <option value="CONFIRMED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="FAILED">Failed</option>
        </select>
        <button className="btn-primary" onClick={load}>Refresh</button>
      </div>
      {error ? <div className="error">{error}</div> : null}

      {stats ? (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="card">Pending: {stats.pending?.count || 0}</div>
          <div className="card">Approved: {stats.confirmed?.count || 0}</div>
          <div className="card">Rejected: {stats.rejected || 0}</div>
        </div>
      ) : null}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Amount</th>
                <th>Network</th>
                <th>Status</th>
                <th>Tx Hash</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.user?.email || item.userId}</td>
                  <td>{item.amount}</td>
                  <td>{item.network}</td>
                  <td>{item.status}</td>
                  <td>{item.txHash || "-"}</td>
                  <td>
                    <div className="row">
                      {item.status === "PENDING_ADMIN_APPROVAL" ? (
                        <>
                          <button className="btn-primary" onClick={() => approve(item.id)}>Approve</button>
                          <button className="btn-danger" onClick={() => reject(item.id)}>Reject</button>
                        </>
                      ) : null}
                      {item.status === "FAILED" ? (
                        <button onClick={() => retry(item.id)}>Retry</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPageChange={(nextPage) =>
            setData((prev) => ({
              ...prev,
              page: nextPage,
            }))
          }
        />
      </div>
    </div>
  );
}
